import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/apiClient";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { getContinent } from "../lib/regions";
import RecentCelebrations from "../components/landing/RecentCelebrations";
import FoundersLeaderboard from "../components/landing/FoundersLeaderboard";
import MostSavedRail from "../components/landing/MostSavedRail";
import XLandingFunnel from "../components/landing/XLandingFunnel";
// FeedPlayerOverlay is no longer rendered here — moved to PlayerLayout
// via PersistentOverlay as part of the Iter-45 state lift.
import FirstTimeBookmarkTooltip from "../components/feed/FirstTimeBookmarkTooltip";
import { usePlayer } from "../context/PlayerContext";
import { FeedLandingTV } from "./feed/FeedLandingTV";
import { FeedActionButtons } from "./feed/FeedActionButtons";
import { FeedTrendingRow } from "./feed/FeedTrendingRow";
import { useFeedSource } from "./feed/useFeedSource";
import { useFeedChat } from "./feed/useFeedChat";
import { useFeedShare } from "./feed/useFeedShare";
import { useFeedActions } from "./feed/useFeedActions";
import { useFeedPlayback } from "./feed/useFeedPlayback";

// FeedPage — the public landing TV. Mode is determined by
// `initialSource`: 'all' = global feed (default, mounted at /),
// 'following' = the Following-only feed (mounted at /feed via the
// MyFeedPage wrapper). Everything else is identical between the two
// surfaces; we just seed the toggle.
const FeedPage = ({ initialSource = 'all' }) => {
  const { user, token, updateCredits } = useAuth();
  const navigate = useNavigate();

  // Data fetches (main feed + featured + trending + source-coercion +
  // reset-event listener) are bundled into a single hook.
  const {
    source, setSource,
    videos, setVideos,
    featuredVideos,
    trendingVideos,
    loading,
  } = useFeedSource({ initialSource, token, user });

  // Playback state lifted into PlayerContext so the FeedPlayerOverlay
  // and its <video> element survive route transitions to /:username
  // and back. See /app/frontend/src/context/PlayerContext.jsx.
  const {
    videoRef,
    userPausedRef,
    failedIdsRef,
    exhaustedToastShownRef,
    isPlaying, setIsPlaying,
    currentVideoIndex, setCurrentVideoIndex,
    setBundle,
  } = usePlayer();

  // Track DISTINCT video IDs the user has viewed this session — used
  // by FirstTimeBookmarkTooltip to trigger the hint after 3 views.
  const [viewedVideoIds, setViewedVideoIds] = useState(() => new Set());
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'homeChat' or 'comment' or 'trail'
  const [liveChatTtlDays, setLiveChatTtlDays] = useState(20);

  // Defensive: `videos` SHOULD be an array but a flaky API response
  // (e.g. an HTML error page or `{detail: "..."}` payload from the
  // backend reaching us through Capacitor's WKWebView) can momentarily
  // set state to a non-array. Coerce to [] so `.forEach` / `.length`
  // always work and the player never crashes on first render in the
  // iOS / Android binary.
  const safeVideos = React.useMemo(
    () => (Array.isArray(videos) ? videos : []),
    [videos]
  );

  // Compute regional video counts. useMemo'd so the giant setBundle
  // effect doesn't churn on every render (it depends on `regionCounts`).
  const regionCounts = React.useMemo(() => {
    const counts = { Global: safeVideos.length, Americas: 0, Asia: 0, Africa: 0, Europe: 0, Oceania: 0 };
    safeVideos.forEach(v => {
      const c = getContinent(v?.location);
      if (counts[c] !== undefined) counts[c] += 1;
    });
    return counts;
  }, [safeVideos]);

  // useFeedPlayback owns activeRegion + the region-filtered activeVideos
  // + isLoopMode, showComments, showCreditsPrompt + all playlist-
  // navigation handlers.
  const playback = useFeedPlayback({
    safeVideos,
    setVideos,
    currentVideoIndex, setCurrentVideoIndex,
    setIsPlaying,
    userPausedRef,
    failedIdsRef,
    exhaustedToastShownRef,
    videoRef,
    setSource,
  });
  const {
    activeVideos,
    isLoopMode, setIsLoopMode,
    showComments, setShowComments,
    showCreditsPrompt, setShowCreditsPrompt,
    activeRegion, setActiveRegion,
    handlePlayAll, handleSkipNext, handleVideoEnded,
    handleClosePlayer, findNextPlayableIndex,
  } = playback;
  const activeVideo = activeVideos[currentVideoIndex];

  // Home-chat fetch + submit + admin-delete handlers, bundled.
  const {
    homeChat,
    newHomeChat, setNewHomeChat,
    submittingHomeChat,
    handleSubmitHomeChat,
    handleDeleteHomeChat,
  } = useFeedChat({
    user, updateCredits,
    setShowSignInPrompt, setPendingAction, setShowCreditsPrompt,
  });

  // Viewer actions: applaud / comment / follow / trail-toggle + comment
  // fetch & submit, plus the trailIds / comments / followingUsers state
  // they mutate.
  const actions = useFeedActions({
    user, token, updateCredits, activeVideo,
    prompts: { setShowSignInPrompt, setPendingAction, setShowCreditsPrompt, setShowComments, showComments },
  });
  const {
    trailIds, comments, newComment, setNewComment, loadingComments, submittingComment,
    followingUsers,
    handleApplaud, handleFollowFromFeed, handleToggleTrail, handleSubmitComment, handleSubmitReply,
    fetchComments,
  } = actions;

  const { handleShare: handleShareCore } = useFeedShare();
  const handleShare = React.useCallback(
    () => handleShareCore(activeVideo, { isLoopMode }),
    [handleShareCore, activeVideo, isLoopMode]
  );

  // ===== Effects =====
  // Track guest video views for the 3-video conversion gate. Only counts
  // unauthenticated users + only when the index actually changes to a
  // playable video. Authenticated users never increment.
  const lastCountedIndexRef = React.useRef(-1);
  React.useEffect(() => {
    if (user) return;
    if (!isPlaying) return;
    if (currentVideoIndex === lastCountedIndexRef.current) return;
    lastCountedIndexRef.current = currentVideoIndex;
    import("../components/auth/SignupGate").then((m) => m.incrementGuestVideoPlay());
  }, [user, isPlaying, currentVideoIndex]);

  // (Recently-watched tracker is registered later, AFTER activeVideos is
  // declared — placing it here would create a temporal-dead-zone
  // ReferenceError on the dependency array's initial evaluation.)
  const recentTrackedRef = React.useRef(-1);

  // Programmatic play: kicks the current video to play on currentVideoIndex
  // change. Replaces the <video autoPlay> attribute which on iOS WKWebView
  // (Capacitor) was being re-asserted by React reconciliation on every
  // parent re-render, causing the user's manual pause to flip back to
  // play half a second later. CRITICAL: also gated on userPausedRef so a
  // tab visibility change or re-render never overrides a user's pause.
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (!isPlaying) return;
    if (userPausedRef.current) return;
    const promise = v.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(() => { /* user-initiated pause / iOS gesture lock */ });
    }
  }, [currentVideoIndex, isPlaying]);

  // Header logo also forces the player back to index 0 + closes it, so a
  // user stuck on an empty Following view who clicks Home gets the full
  // reset experience. The hook resets `source` via its own listener; we
  // ride alongside it for the player-state side effects.
  useEffect(() => {
    const handler = () => {
      setCurrentVideoIndex(0);
      setIsPlaying(false);
    };
    window.addEventListener("globaleyes:reset-feed-source", handler);
    return () => window.removeEventListener("globaleyes:reset-feed-source", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch global Live Chat TTL setting (admin-managed)
  useEffect(() => {
    const fetchTtl = async () => {
      try {
        const res = await api.get(`/settings/live-chat`);
        if (res.data?.ttl_days) setLiveChatTtlDays(res.data.ttl_days);
      } catch (err) {
        console.error("Failed to fetch live chat TTL:", err);
      }
    };
    fetchTtl();
  }, []);

  // Hydrate the user's saved-to-Trail set on mount (and on token change).
  // Cheap call (returns just IDs, capped at 500) so we can run it once
  // per session without instrumentation. Logged-out users skip — they
  // have no trail by definition.
  useEffect(() => {
    if (!user || !token) {
      actions.setTrailIds(new Set());
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/users/me/trail/ids`);
        if (!cancelled) actions.setTrailIds(new Set(res.data?.ids || []));
      } catch (_err) { /* silent — bookmark just stays empty */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  // Fetch comments when video changes or comments panel opens
  useEffect(() => {
    if (isPlaying && activeVideo) {
      // Track view for FirstTimeBookmarkTooltip's "after 3 distinct
      // videos" gate. Idempotent — Set semantics dedupe.
      setViewedVideoIds(prev => prev.has(activeVideo.id) ? prev : new Set(prev).add(activeVideo.id));
      fetchComments(activeVideo.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, currentVideoIndex]);

  // Reset playback index when the region filter changes the active set
  React.useEffect(() => {
    if (currentVideoIndex >= activeVideos.length) {
      setCurrentVideoIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRegion, activeVideos.length]);

  // Track the last 5 videos this device watched for the Producer
  // Marketplace "Make an Offer on a Video" panel. Fires after 2s of
  // continuous playback so casual scroll-throughs don't pollute the
  // list — the user has to actually dwell on a video for it to count
  // as "watched". Storage is localStorage (per-device).
  React.useEffect(() => {
    if (!isPlaying) return undefined;
    if (!activeVideo) return undefined;
    if (recentTrackedRef.current === currentVideoIndex) return undefined;
    const t = setTimeout(() => {
      recentTrackedRef.current = currentVideoIndex;
      import("../lib/recentWatched").then((m) => m.pushRecentWatched(activeVideo));
    }, 2000);
    return () => clearTimeout(t);
  }, [isPlaying, currentVideoIndex, activeVideo]);

  // Iter-80 (2026-06-18): landing page is now firmly locked — no
  // scroll, no parallax, no founding-spot badges. Everything fits
  // inside one 100dvh viewport on every device. The user gets one
  // simple choice: tap Play, tap Explore Map, or tap Create Video.
  const creditsPromptConfig = {
    applaud: { amount: '0.10', action: 'Applaud this video' },
    comment: { amount: '0.05', action: 'View comments' },
  };

  // Push the page-specific data + handlers bundle into PlayerContext.
  // PlayerLayout's PersistentOverlay reads from context and renders
  // ONE shared FeedPlayerOverlay across all player routes — so the
  // <video> DOM element survives `/ → /:username` navigation.
  //
  // CRITICAL: setBundle is only called when isPlaying === true. If we
  // ran it on every render, the new object reference would re-trigger
  // the provider's setState → re-render this page → infinite loop
  // ("Maximum update depth exceeded"). Guarding on isPlaying means we
  // only churn while the overlay is actually on screen.
  React.useEffect(() => {
    if (!isPlaying) {
      setBundle(null);
      return;
    }
    setBundle({
      mode: 'home',
      activeVideos,
      videos,
      isLoopMode, setIsLoopMode,
      source, setSource,
      setIsPlaying,
      activeRegion, setActiveRegion,
      regionCounts,
      findNextPlayableIndex,
      handleClosePlayer,
      handleVideoEnded,
      handleSkipNext,
      handleShare,
      handleApplaud,
      handleFollowFromFeed,
      handleSubmitComment,
      handleSubmitReply,
      handleSubmitHomeChat,
      handleDeleteHomeChat,
      comments,
      loadingComments,
      newComment, setNewComment,
      submittingComment,
      homeChat,
      newHomeChat, setNewHomeChat,
      submittingHomeChat,
      liveChatTtlDays,
      featuredVideos,
      followingUsers,
      trailIds,
      handleToggleTrail,
      showSignInPrompt, setShowSignInPrompt,
      pendingAction, setPendingAction,
      showCreditsPrompt, setShowCreditsPrompt,
      creditsPromptConfig,
      user,
      navigate,
    });
    // Explicit dep list — only re-push when state slices that actually
    // affect the overlay UI change. Avoids the infinite render loop
    // every-render setBundle would cause.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPlaying, activeVideos, videos, isLoopMode, source, activeRegion,
    regionCounts, comments, loadingComments, newComment, submittingComment,
    homeChat, newHomeChat, submittingHomeChat, featuredVideos,
    followingUsers, trailIds, showSignInPrompt, pendingAction,
    showCreditsPrompt, user,
  ]);

  // Clear the bundle when the page unmounts (route change away). The
  // persistent overlay then de-renders cleanly.
  React.useEffect(() => {
    return () => setBundle(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Iter-83 — TRULY lock the landing page. While the landing hero is
  // visible (i.e. !isPlaying), forcibly disable body scroll so neither
  // the page content NOR the Header can drift on mobile. The cleanup
  // restores normal scroll the moment the player takes over.
  React.useEffect(() => {
    if (isPlaying) return;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [isPlaying]);

  return (
    <div className="w-full overflow-x-hidden bg-gradient-to-b from-gray-950 to-black" data-testid="feed-page">
      {/* The FeedPlayerOverlay is rendered by PlayerLayout via the
          PersistentOverlay component — see PlayerContext.jsx. FeedPage
          only renders the tooltip + the landing chrome below. */}
      {isPlaying && (
        <FirstTimeBookmarkTooltip videoViews={viewedVideoIds.size} user={user} />
      )}

      {/* LANDING — Iter-83 truly locked. justify-start + tight top padding
          so content sits high under the header, no acres of dead space.
          Body overflow is forced hidden via the useEffect above so the
          header CANNOT scroll out of view on iOS. */}
      {!isPlaying && (
      <div className="relative h-[calc(100dvh-4rem)] w-full max-w-full flex flex-col items-center justify-start pt-4 sm:pt-8 px-6 sm:px-4 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/20 via-gray-900 to-purple-900/20" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-500/5 via-transparent to-transparent" />

        <div className="relative z-10 text-center w-full max-w-full">
          {/* X-funnel welcome banner — silently no-ops unless the
              visitor arrived via a tracked utm_source=x link. */}
          <XLandingFunnel user={user} />

          {/* Tagline above TV — text-xs on mobile (smaller per Eric's
              iter-83 feedback), bumps up to text-sm on tablet+. Tight
              bottom margin so the TV sits close. */}
          <p className="text-gray-400 text-xs sm:text-sm mb-2 sm:mb-4 tracking-wide">eliciting world views</p>

          {/* TV Display — static, no parallax. */}
          <FeedLandingTV
            loading={loading}
            activeVideos={activeVideos}
            activeRegion={activeRegion}
            source={source}
            onPlayAll={handlePlayAll}
          />

          {/* Action Buttons */}
          <FeedActionButtons user={user} />
        </div>
      </div>
      )}
      {/* Social-proof rails — rendered ONLY during playback so they
          don't compete with the landing's Play CTA. Each rail
          self-hides when its dataset is empty so a quiet day still
          looks clean. */}
      {isPlaying && (
        <>
          <FeedTrendingRow
            videos={trendingVideos}
            onSelectVideo={(v) => {
              handleClosePlayer();
              navigate(`/${v.username}?video=${v.id}`);
            }}
          />
          <RecentCelebrations />
          <FoundersLeaderboard />
          <MostSavedRail />
          <Footer />
        </>
      )}
    </div>
  );
};

export default FeedPage;
